var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { RedesignCreatorMultiSelect } from 'root/src/client/v2/_pages/drop/redesign/RedesignCreatorMultiSelect';
import { useCreateBrandDare, getCreateBrandDareErrorMessage } from 'root/src/client/v2/_pages/drop/redesign/useCreateBrandDare';
// The legacy form capped the assignee picker at 10 creators per dare; the endpoint creates one
// project holding all of them, so the cap is what keeps a single project from ballooning.
const MAX_CREATORS = 10;
// Brand/admin counterpart to the creator "Accept dare" flow: instead of taking the dare on, the drop
// owner hands it to creators of their choosing. Replaces the legacy /create-brand-project page (the
// "Dare a Creator" form), which this reproduces inline: the dare is fixed and shown read-only, the
// only decision is who gets it.
export const RedesignCreateBrandDareModal = memo(({ dropId, dare, rewardName, onCreated }) => {
    const modal = useRedesignModal();
    const [creators, setCreators] = useState([]);
    const { loading, success, error, run } = useAsyncAction();
    const createBrandDare = useCreateBrandDare();
    const onSubmit = () => {
        var _a, _b;
        if (creators.length === 0) {
            return;
        }
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield createBrandDare.mutateAsync({ dropId, dare, creators });
            yield onCreated();
        }), {
            onSuccess: modal.hide,
            toast: {
                loading: creators.length > 1 ? 'Daring creators...' : 'Daring creator...',
                success: creators.length > 1
                    ? `Dare sent to ${creators.length} creators.`
                    : `Dare sent to ${(_b = (_a = creators[0]) === null || _a === void 0 ? void 0 : _a.displayName) !== null && _b !== void 0 ? _b : 'the creator'}.`,
                error: getCreateBrandDareErrorMessage,
            },
        });
    };
    return (<RedesignModalShell className="sm:max-w-lg">
			<div className="flex w-full flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Dare a creator</h2>
				<p className="text-sm text-muted-foreground">Pick who should take this dare on. They get it straight in their dares list.</p>
			</div>

			<div className="w-full rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4">
				<h3 className="text-sm font-bold text-foreground">{dare.title}</h3>
				<p className="mt-1 text-xs text-muted-foreground">{dare.description}</p>
				<span className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-solid border-neon-amber/40 bg-neon-amber/10 px-2 py-1 text-[11px] font-semibold text-neon-amber">
					<EmojiEventsIcon sx={{ fontSize: 13 }}/>
					{dare.paymentInfo.amount}x {rewardName}
				</span>
			</div>

			<div className="w-full">
				<Field label="Creators" info={`Up to ${MAX_CREATORS} creators get this dare at once.`}>
					<RedesignCreatorMultiSelect value={creators} onChange={setCreators} maxItems={MAX_CREATORS}/>
				</Field>
			</div>

			<Button type="button" variant="brand" onClick={onSubmit} disabled={creators.length === 0} loading={loading} success={success} error={error} className="w-full rounded-lg text-sm font-semibold">
				{creators.length > 1 ? `Dare ${creators.length} creators` : 'Create dare'}
			</Button>
			<p className="text-center text-[11px] text-muted-foreground">
				Each creator gets an email about the dare. Rewards are only handed out once they deliver.
			</p>
		</RedesignModalShell>);
});
